// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useRequestBurn2Step } from '@hooks/mutations/registry/burn/useRequestBurn2Step';
import { useGetPrepareTwoStepTransfer } from '@hooks/other/useGetPrepareTwoStepTransfer';
import { useCallback, useState } from 'react';
import { Holding } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Holding';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { InstrumentSelections } from '../../../components/InstrumentSelections';
import { useParties } from '../../../contexts/PartiesContext';
import TemplateContract from '../../../utils/TemplateContract';
import { getValidateAmountMessage } from '../../../utils/getValidateAmountMessage';
import { MaxLimitErrorMessage } from '../transfer/MaxLimitErrorMessage';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  holding?: TemplateContract<Holding>;
  disableDialog?: boolean;
};

export const RequestBurnDialog = ({ disableDialog, open, onClose, holding }: DialogProps) => {
  const [instrumentId, setInstrumentId] = useState<string>(holding?.payload.instrument.id || '');
  const [amount, setAmount] = useState<string>(holding?.payload.amount || '');
  const [label, setLabel] = useState<string>(holding?.payload.label || '');
  const [registrar, setRegistrar] = useState<string>(holding?.payload.registrar || '');
  const [reference, setReference] = useState<string>('');
  const { party } = useParties();
  const [showMaxLimitError, setShowMaxLimitError] = useState(false);

  const [burnAmountValidationMsg, setBurnAmountValidationMsg] = useState<string>('');

  const twoStepBurn = useRequestBurn2Step();
  const prepareTwoStepTransfer = useGetPrepareTwoStepTransfer();

  const resetState = useCallback(() => {
    setInstrumentId('');
    setAmount('');
    setLabel('');
    setRegistrar('');
    setReference('');
  }, [setInstrumentId, setAmount, setLabel, setRegistrar, setReference]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);
  const getBurnValidationMsg = getValidateAmountMessage('Burn', holding?.payload.amount);

  const twoStepBurnRequest = async () => {
    setShowMaxLimitError(false);
    const { holdingCids, exceedsMaxLimit } = prepareTwoStepTransfer({
      instrumentId,
      registrar,
      amount,
      owner: party,
    });
    if (exceedsMaxLimit) {
      setShowMaxLimitError(true);
      return;
    }
    await twoStepBurn({
      party,
      amount,
      source: registrar,
      instrumentId,
      holdingCids,
      reference,
    });
    handleClose();
  };
  const onInstrumentChange = (instId: string, instRegistrar: string) => {
    setInstrumentId(instId);
    setRegistrar(instRegistrar);
  };

  const disableSubmit = !!burnAmountValidationMsg.length || !instrumentId || !amount || !registrar;
  return (
    <FormDialog
      disableDialog={disableDialog}
      open={open}
      title="Request Burn"
      onCancel={handleClose}
      onSubmit={twoStepBurnRequest}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    >
      <InstrumentSelections
        instrumentId={holding?.payload.instrument.id}
        registrar={holding?.payload.instrument.source}
        onInstrumentChange={holding ? undefined : onInstrumentChange}
      />
      <TextInput
        type="number"
        validationMessage={burnAmountValidationMsg}
        setValidationMessage={setBurnAmountValidationMsg}
        getValidationMessage={getBurnValidationMsg}
        label="Amount"
        value={amount}
        setValue={setAmount}
        required
      />
      <TextInput label="Label" value={label} setValue={setLabel} disabled={!!holding} hidden />

      {showMaxLimitError && <MaxLimitErrorMessage />}

      <TextInput label="Reference" value={reference} setValue={setReference} />
    </FormDialog>
  );
};
